/**
 * The import in the interface (decision 40; part 18 log, tables V and Y): Import… beside New book with its file picker,
 * the import's status under the toolbar as the PDF's and the bundle's are, and the review dialog: the book's title,
 * its chapters and what to check, Import book and Cancel. What the status says is announced once per phase (A13).
 */
import { LONG_CHAPTER_WORDS } from '@cockatrice/core';
import type { FlagKind, ImportFlag } from '@cockatrice/core';
import type { Messages } from '../i18n/en.ts';
import type { ImportFailure, ImportState, Review } from '../import/controller.ts';
import { IMPORT_ACCEPT } from '../import/kind.ts';
import { focusOn } from './announce.ts';
import type { MenuItem } from '../ui/Menu.ts';
import { importing } from './bundle-import.ts';
import { each, el, when } from '../ui/dom.ts';
import Button from '../ui/Button.ts';
import Icon from '../ui/Icon.ts';
import List, { ListItem } from '../ui/List.ts';
import { component } from '../ui/solid.ts';
import TextField from '../ui/TextField.ts';
import { ModalActions, ModalBody, ModalFoot } from './modal.ts';
import type { Child, On } from '../ui/dom.ts';
import type { Shell } from './shell.ts';

/** V4, V11: why an import gave no book, in words. */
export function failureText(shell: Pick<Shell, 't'>, reason: ImportFailure): string {
  const { t } = shell;
  switch (reason) {
    case 'not-supported':
      return t('import.notSupported');
    case 'too-large':
      return t('import.tooLarge');
    case 'not-zip':
      return t('import.notZip');
    case 'not-docx':
      return t('import.notDocx');
    case 'damaged':
    case 'bad-name':
      return t('import.damaged');
    case 'unsupported':
      return t('import.unsupported');
    case 'too-many-files':
    case 'too-big':
      return t('import.tooBig');
    case 'unreadable':
      return t('import.unreadable');
    case 'empty':
      return t('import.empty');
    case 'expands':
      return t('import.expands');
    case 'own-export':
      return t('import.ownExport');
    case 'timed-out':
      return t('import.timedOut');
    case 'busy':
      return t('import.busy');
    case 'not-editable':
      return t('import.notEditable');
    case 'save-failed':
      return t('import.saveFailed');
    case 'stale':
      return t('import.stale');
    case 'write-failed':
      return t('import.writeFailed');
    // Part 24b's bundle problems (not-bundle, newer, file-changed): Import… never reads a bundle (K), so none reaches
    // it; part 24c says them in words.
    case 'cancelled':
    case 'failed':
    case 'not-bundle':
    case 'newer':
    case 'file-changed':
      return t('import.failed');
  }
}

/** What the status says (V-rows' outcomes); nothing while idle or under review. */
export function importText(shell: Pick<Shell, 't'>, state: ImportState): string {
  const { t } = shell;
  switch (state.kind) {
    case 'idle':
    case 'review':
      return '';
    case 'reading':
      return t('import.reading', { name: state.fileName, percent: state.total === 0 ? 0 : Math.floor((100 * state.done) / state.total) });
    case 'adding':
      return t('import.adding', { done: state.done, total: state.total });
    case 'storing':
      return t('import.storing');
    case 'done':
      return [t('import.done', { title: state.title, count: state.chapters }), ...(state.missing > 0 ? [t('import.missing', { count: state.missing })] : [])].join(' ');
    case 'failed':
      return failureText(shell, state.reason);
  }
}

/** A13: what the import announces: its phases, never each percent or picture the status shows. */
export function importNews(shell: Pick<Shell, 't'>, state: ImportState): string {
  if (state.kind === 'reading') return shell.t('import.readingNews');
  if (state.kind === 'adding') return shell.t('import.addingNews');
  return importText(shell, state);
}

/**
 * Y: Import… as a menu's item (the library's Menu), with its own file picker (hidden, kept beside the menu so it outlives
 * the list closing; the item opens it), off while the book can't change or one runs.
 */
export function ImportItem(shell: Shell, look: Pick<MenuItem, 'label' | 'hint'>): { readonly item: MenuItem; readonly picker: Child } {
  const { workspace, importer } = shell;
  let picker: HTMLInputElement | undefined;
  return {
    item: {
      class: 'import-book',
      label: look.label,
      hint: look.hint,
      // Part 23b, V′2; part 24c: one file through the sandbox at a time, an import, a re-import or a bundle.
      get disabled() {
        return !workspace.writable() || importing(shell);
      },
      onSelect: () => picker?.click(),
    },
    picker: el('input', {
      type: 'file',
      class: 'import-picker',
      accept: IMPORT_ACCEPT,
      hidden: true,
      tabIndex: -1,
      'aria-hidden': 'true',
      ref: (input: HTMLInputElement) => {
        picker = input;
      },
      onChange: (event: On<Event, HTMLInputElement>) => {
        const file = event.currentTarget.files?.[0];
        // The same file chosen again must still fire a change.
        event.currentTarget.value = '';
        if (file === undefined) return;
        importer.pick(file);
        focusOn(shell, '.cancel-import');
      },
    }),
  };
}

/** Whether the status shows: while reading, adding or storing, and after, with the outcome. */
function showing(state: ImportState): boolean {
  return state.kind !== 'idle' && state.kind !== 'review';
}

/** V3, V4, V9, V10: the status region; Cancel and Dismiss leave the focus on Import… (A24). */
export function ImportStatus(shell: Shell): Child {
  const { t, importer } = shell;
  const state = (): ImportState => importer.state();
  const running = (): boolean => state().kind === 'reading' || state().kind === 'adding';
  return when(
    () => showing(state()),
    () =>
      el(
        'section',
        { class: 'export-status import-status', 'aria-label': t('import.label') },
        el('p', {}, () => importText(shell, state())),
        () => {
          const now = state();
          return now.kind === 'reading' || now.kind === 'adding' ? el('progress', { max: Math.max(1, now.total), value: now.done }) : null;
        },
        when(running, () =>
          el(
            'button',
            {
              type: 'button',
              class: 'cancel-import',
              onClick: () => {
                importer.cancel();
                focusOn(shell, '.import-book');
              },
            },
            t('import.cancel'),
          ),
        ),
        when(
          () => state().kind === 'done' || state().kind === 'failed',
          () =>
            el(
              'button',
              {
                type: 'button',
                class: 'dismiss-import',
                onClick: () => {
                  importer.dismiss();
                  focusOn(shell, '.import-book');
                },
              },
              t('export.dismiss'),
            ),
        ),
      ),
  );
}

/** FL: the flags said once for the whole file, whatever their count. */
const SINGLE_FLAGS = {
  'macros': 'import.flag.macros',
  'not-split': 'import.flag.not-split',
  'encoding': 'import.flag.encoding',
} as const satisfies Partial<Record<FlagKind, keyof Messages>>;

/** FL: the flags said with their count. */
const COUNTED_FLAGS = {
  'table': 'import.flag.table',
  'tracked': 'import.flag.tracked',
  'comments': 'import.flag.comments',
  'hidden': 'import.flag.hidden',
  'underline': 'import.flag.underline',
  'strike': 'import.flag.strike',
  'superscript': 'import.flag.superscript',
  'subscript': 'import.flag.subscript',
  'caps': 'import.flag.caps',
  'link': 'import.flag.link',
  'contents': 'import.flag.contents',
  'embedded': 'import.flag.embedded',
  'graphic': 'import.flag.graphic',
  'equation': 'import.flag.equation',
  'symbol': 'import.flag.symbol',
  'textbox': 'import.flag.textbox',
  'endnotes': 'import.flag.endnotes',
  'missing-note': 'import.flag.missing-note',
  'deep-heading': 'import.flag.deep-heading',
  'linked-image': 'import.flag.linked-image',
  'image-format': 'import.flag.image-format',
  'image-large': 'import.flag.image-large',
  'image-missing': 'import.flag.image-missing',
  'image-count': 'import.flag.image-count',
  'structure': 'import.flag.structure',
  'formatting-kept-out': 'import.flag.formatting-kept-out',
  'code': 'import.flag.code',
  'html': 'import.flag.html',
  'indented': 'import.flag.indented',
  'outside-image': 'import.flag.outside-image',
  'pandoc': 'import.flag.pandoc',
} as const satisfies Record<Exclude<FlagKind, keyof typeof SINGLE_FLAGS>, keyof Messages>;

/** Whether `kind` is said once for the whole file. */
function isSingle(kind: FlagKind): kind is keyof typeof SINGLE_FLAGS {
  return kind in SINGLE_FLAGS;
}

/** FL: one flag in words, with the chapters it touches when the book has more than one. */
export function flagText(shell: Pick<Shell, 't'>, flag: ImportFlag, chapters: number): string {
  const { t } = shell;
  const { kind } = flag;
  const said = isSingle(kind) ? t(SINGLE_FLAGS[kind]) : t(COUNTED_FLAGS[kind], { count: flag.count });
  if (chapters < 2 || flag.chapters.length === 0) return said;
  return `${said} ${t('import.where', { list: flag.chapters.map((index) => String(index + 1)).join(', ') })}`;
}

/** V5–V7, V13: the review dialog's body (PLAN.md §3, step 4: the handoff's "Check the chapters", from the library). */
export function ImportReview(shell: Shell, review: Review): Child {
  const { t, workspace, importer, ui } = shell;
  const { book } = review;
  let title = review.title;
  const words = book.chapters.reduce((sum, chapter) => sum + chapter.words, 0);
  return [
    ModalBody(
      { class: 'import-review' },
      el('p', {}, t('import.summary', { count: book.chapters.length, words })),
      el('p', {}, t('import.newBook')),
      component(TextField, {
        id: 'import-title',
        label: t('import.bookTitle'),
        defaultValue: review.title,
        onChange: (value: string) => {
          title = value;
        },
      }),
      el('h3', {}, t('import.chapters')),
      component(List, {
        as: 'ol',
        class: 'import-chapters',
        get children() {
          return each(
            () => book.chapters,
            (chapter, index) =>
              component(ListItem, {
                // The list is ordered; the number the design shows is for the eye.
                leading: el('span', { class: 'import-chapter-number', 'aria-hidden': 'true' }, String(index() + 1)),
                title: chapter.title === null || chapter.title === '' ? t('chapter.untitled') : chapter.title,
                trailing: t('import.words', { count: chapter.words }),
                // V5b (decision 14, deep-fix r3): a chapter past the long-chapter limit is said so before it is stored.
                description:
                  chapter.words > LONG_CHAPTER_WORDS ? el('strong', { class: 'import-long' }, component(Icon, { name: 'warn', size: 'sm' }), t('import.long', { words: LONG_CHAPTER_WORDS })) : undefined,
              }),
          );
        },
      }),
      review.pictures.length > 0 ? el('p', {}, t('import.pictures', { count: review.pictures.length })) : null,
      book.flags.length > 0
        ? [
            el('h3', {}, t('import.check')),
            el('ul', { class: 'import-flags' }, each(() => book.flags, (flag) => el('li', {}, component(Icon, { name: 'warn', size: 'sm' }), el('span', {}, flagText(shell, flag, book.chapters.length))))),
          ]
        : null,
    ),
    ModalFoot(
      {},
      ModalActions(
        component(Button, {
          onClick: () => {
            importer.closeReview();
            ui.setDialog(null);
          },
          children: t('import.cancel'),
        }),
        component(Button, {
          variant: 'primary',
          class: 'confirm-import',
          get disabled() {
            return !workspace.writable();
          },
          onClick: () => {
            // Confirmed before the dialog closes: its close (V6) puts away only a review still waiting.
            const stored = importer.confirm(title);
            ui.setDialog(null);
            // A24: the status's Cancel while pictures are added; then the new book's text, or the failure's Dismiss.
            focusOn(shell, '.cancel-import');
            void stored.then(() => {
              // Part 11d, R8: the book imported from home is open in the writer, as the status says.
              if (importer.state().kind === 'done') {
                shell.go('write');
                shell.focusEditor();
              } else focusOn(shell, '.dismiss-import');
            });
          },
          children: t('import.confirm'),
        }),
      ),
    ),
  ];
}
